import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FileText, FileCheck, LayoutDashboard, LogOut, User, Menu, X, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Logo, LogoMark } from '@/components/ui/logo';
import { useAuthStore } from '@/features/auth/store/authStore';
import { authService } from '@/features/auth/services/authService';
import { cn, formatDisplayName, getDisplayInitial } from '@/lib/utils';
import { WhatsAppChatSupport } from '@/components/WhatsAppChatSupport';
import { TaxYearSelector, useTaxYear } from '@/features/tax-year';

interface MainLayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/file-tax', label: 'Forms', icon: ClipboardList },
  { path: '/files', label: 'Documents', icon: FileText },
  { path: '/tax-filings', label: 'Tax Filings', icon: FileCheck },
];

export const MainLayout = ({ children }: MainLayoutProps) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Ensure tax year config is fetched for all pages under this layout
  useTaxYear();

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b-2 border-zinc-300 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center gap-6 lg:gap-8">
              {/* Logo */}
              <Link
                to="/dashboard"
                className="flex items-center transition-opacity hover:opacity-90"
              >
                {/* Show full logo on md+ screens, mark only on smaller */}
                <div className="hidden sm:block">
                  <Logo size="md" />
                </div>
                <div className="sm:hidden">
                  <LogoMark size="md" />
                </div>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.path === '/file-tax'
                    ? location.pathname === '/file-tax' || location.pathname.startsWith('/file-tax/')
                    : item.path === '/tax-filings'
                      ? location.pathname === '/tax-filings' || location.pathname.startsWith('/tax-filings/')
                      : location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path}>
                      <Button
                        variant={isActive ? 'secondary' : 'ghost'}
                        size="sm"
                        className={cn(
                          'gap-2 font-medium h-9 px-4',
                          isActive && 'bg-black text-white font-semibold shadow-sm border-black'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Right side - Tax Year + User Menu */}
            <div className="flex items-center gap-2">
              <TaxYearSelector />
              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-2 h-10 hover:bg-accent"
                  >
                    <Avatar className="h-8 w-8 border border-border">
                      <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm">
                        {getDisplayInitial(user)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium hidden sm:inline max-w-[150px] truncate">
                      {formatDisplayName(user) || user?.userId}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56"
                >
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-black">{formatDisplayName(user) || user?.userId}</p>
                      <p className="text-xs text-zinc-600 truncate">
                        {user?.userId}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-10 w-10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t-2 border-zinc-300 bg-white text-black">
            <nav className="container mx-auto px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.path === '/file-tax'
                    ? location.pathname === '/file-tax' || location.pathname.startsWith('/file-tax/')
                    : item.path === '/tax-filings'
                      ? location.pathname === '/tax-filings' || location.pathname.startsWith('/tax-filings/')
                      : location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full justify-start gap-3 h-11 text-base font-medium',
                        isActive && 'bg-black text-white font-semibold shadow-sm border-black'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content - flex so footer stays at bottom; overflow for scroll */}
      <main className="flex-1 min-h-0 overflow-auto">{children}</main>

      {/* Footer - compact */}
      <footer className="border-t-2 border-zinc-300 bg-white text-black  py-3">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <LogoMark size="sm" />
              <span className="text-xs text-muted-foreground">
                My Professional Tax
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} My Professional Tax. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <WhatsAppChatSupport />
    </div>
  );
};
